import type { IqDisplayState } from '../../../domain/IqDisplayState.ts';
import type { RemoteViewLayout } from 'shared/lib/tv-game/nativeLayout.ts';
import { defaultRemoteViewLayout } from 'shared/lib/tv-game/nativeLayout.ts';
import { getRemoteCalibStandZoneMetrics } from 'shared/lib/tv-game/remoteCalibStandZone.ts';
import { drawImageCover } from '../room/primitives.ts';
import { drawCalibBackdrop, drawRemoteCalibrationStandZone } from '../room/calibrationShared.ts';
import { drawLiveSkeletonPts, drawBladeTrails } from '../room/skeleton.ts';
import { useLighterCanvasEffects } from '../room/canvasHelpers.ts';

const IQ_BG = '/media/iqBg.png';

/** IQ game: фон, силуэт калибровки, ответы и floating labels на canvas; HUD — в React. */
export class IqCanvasLayer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly bgImage: HTMLImageElement;
  private bgGradientCache: CanvasGradient | null = null;
  private bgGradientCacheKey = '';

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Cannot get 2D canvas context');
    this.ctx = ctx;
    // src ставится в preload() — фон грузится только когда выбрана эта игра.
    this.bgImage = new Image();
  }

  preload(): void {
    if (!this.bgImage.src) this.bgImage.src = IQ_BG;
  }

  render(state: IqDisplayState): void {
    if (state.phase === 'lobby') {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      return;
    }

    const W = this.canvas.width;
    const H = this.canvas.height;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, W, H);

    if (state.phase === 'calibrating') {
      this.drawCalibrationScreen(state, W, H);
      return;
    }

    this.drawBackground(W, H);
    const layout = state.remoteView ?? defaultRemoteViewLayout(W, H);
    const light = useLighterCanvasEffects(this.canvas);
    // No gameBounds clip — wrists can be below the game rect (y > 1) in IQ
    drawBladeTrails(this.ctx, W, state.bladeTrails, undefined, light);
    this.drawLabels(state, W, H);
  }

  private drawCalibrationScreen(state: IqDisplayState, W: number, H: number): void {
    const ctx = this.ctx;
    const layout = state.remoteView ?? defaultRemoteViewLayout(W, H);

    this.drawBackground(W, H);

    ctx.save();
    ctx.beginPath();
    ctx.rect(layout.offsetX, layout.offsetY, layout.gameWidth, layout.gameHeight);
    ctx.clip();

    drawCalibBackdrop(ctx, layout);
    drawLiveSkeletonPts(ctx, state.calibNormPoints, layout, false, 56, 255, 180, useLighterCanvasEffects(this.canvas));
    drawRemoteCalibrationStandZone(ctx, layout, 'upper');
    this.drawDetectionProgressScan(layout, state.detectionProgress);
    ctx.restore();
  }

  private drawDetectionProgressScan(layout: RemoteViewLayout, progress: number): void {
    const p = progress === null || progress === undefined ? 0 : Math.max(0, Math.min(1, progress));
    if (p <= 0) return;
    const ctx = this.ctx;
    const { boxX, boxY, boxW, boxH, borderW } = getRemoteCalibStandZoneMetrics(layout, 'upper');
    const inset = Math.max(4, borderW * 1.2);
    const innerW = boxW - 2 * inset;
    const innerH = boxH - 2 * inset;
    const innerX = boxX + inset;
    const innerY = boxY + inset;
    const fillH = innerH * p;
    const fillY = innerY + innerH - fillH;

    ctx.save();
    const g = ctx.createLinearGradient(innerX, fillY + fillH, innerX, fillY);
    g.addColorStop(0, 'rgba(25, 90, 200, 0.55)');
    g.addColorStop(0.55, 'rgba(55, 140, 255, 0.42)');
    g.addColorStop(1, 'rgba(120, 190, 255, 0.3)');
    ctx.fillStyle = g;
    ctx.fillRect(innerX, fillY, innerW, fillH);
    ctx.restore();
  }

  private drawBackground(W: number, H: number): void {
    const ctx = this.ctx;
    const img = this.bgImage;
    if (img.complete && img.naturalWidth > 0) {
      drawImageCover(ctx, img, 0, 0, W, H);
      return;
    }
    const key = `${W}x${H}`;
    if (!this.bgGradientCache || this.bgGradientCacheKey !== key) {
      const g = ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, '#0d1b2a');
      g.addColorStop(0.5, '#1b2838');
      g.addColorStop(1, '#0a1628');
      this.bgGradientCache = g;
      this.bgGradientCacheKey = key;
    }
    ctx.fillStyle = this.bgGradientCache;
    ctx.fillRect(0, 0, W, H);
  }

  private drawLabels(state: IqDisplayState, W: number, H: number): void {
    if (state.labels.length === 0) return;
    const ctx = this.ctx;
    const fontPx = Math.max(22, Math.round(H * 0.06));
    ctx.font = `900 ${fontPx}px 'Arial Black', Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = Math.max(2, fontPx * 0.1);
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(0,0,0,0.85)';

    for (const lb of state.labels) {
      const a = Math.max(0, Math.min(1, lb.alpha));
      if (a < 0.03) continue;
      const px = lb.nx * W;
      const py = lb.ny * H;
      ctx.globalAlpha = a;
      ctx.fillStyle = lb.color;
      ctx.strokeText(lb.text, px, py);
      ctx.fillText(lb.text, px, py);
    }
    ctx.globalAlpha = 1;
  }

}
